import React, { useState, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Fade from "@mui/material/Fade";
import Alert from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import ImageUploader from "CommonComponents/ImageUploader/ImageUploader.jsx";
import { AuthContext } from "context/Authentication";
import { redirectToLoginWithReturn } from "utils/authUtils";
import {
  uploadRecord,
  handleApiResponse,
  handleApiError,
} from "services/services";
import SnackBar from "CommonComponents/Snackbar/SnackBar.jsx";

const API_UserFilesCreate = "userfiles/create";

const fileTypeOptions = [
  { label: "Image", value: "Image" },
  { label: "PDF", value: "PDF" },
];

const genderOptions = [
  { label: "Male", value: "Male" },
  { label: "Female", value: "Female" },
  { label: "Other", value: "Other" },
];

export default function UserFilesUpload({ open, onClose, onSuccess }) {
  const context = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  // Main form state
  const [fileType, setFileType] = useState("");
  const [fileFor, setFileFor] = useState("");
  const [mediaArray, setMediaArray] = useState([]);

  // File_For_Details state
  const [details, setDetails] = useState({
    Gender: "",
    Age: "",
    Symptoms: "",
    Notes: "",
  });

  // UI state
  const [processing, setProcessing] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackOptions, setSnackOptions] = useState({
    color: "success",
    message: "",
  });

  // Reset all fields on close
  const handleDialogClose = () => {
    setFileType("");
    setFileFor("");
    setMediaArray([]);
    setDetails({ Gender: "", Age: "", Symptoms: "", Notes: "" });
    setProcessing(false);
    setSnackOpen(false);
    setSnackOptions({ color: "success", message: "" });
    onClose && onClose();
  };

  // Controlled change for details
  const handleDetailsChange = (e) => {
    const { name, value } = e.target;
    setDetails((prev) => ({ ...prev, [name]: value }));
  };

  // Save handler
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!fileType || !fileFor || !mediaArray.length) {
      setSnackOptions({
        color: "warning",
        message: "Please fill all required fields and upload a file.",
      });
      setSnackOpen(true);
      return;
    }
    setProcessing(true);

    const formData = new FormData();
    formData.append("User_Id", context.state?.user?.User_Id);
    formData.append("File_Type", fileType);
    formData.append("File_For", fileFor);
    formData.append("File_For_Details", JSON.stringify(details));
    formData.append("Added_By", context.state?.user?.User_Id);
    formData.append("file", mediaArray[0].file);

    uploadRecord(API_UserFilesCreate, formData)
      .then((response) => {
        handleApiResponse(
          response,
          setSnackOptions,
          setProcessing,
          setSnackOpen,
          () => {
            setMediaArray([]);
            setFileType("");
            setFileFor("");
            setDetails({ Gender: "", Age: "", Symptoms: "", Notes: "" });
            setSnackOptions({
              color: "success",
              message: "Prescription uploaded successfully!",
            });
            setSnackOpen(true);
            setProcessing(false);
            setShowSuccessView(true);
            setTimeout(() => {
              setInfoSnackOpen(true);
            }, 1000);
            setTimeout(() => {
              setShowSuccessView(false);
              onSuccess && onSuccess();
              handleDialogClose();
            }, 4000);
          }
        );
      })
      .catch((error) => {
        handleApiError(error, setSnackOptions, setProcessing, setSnackOpen);
      });
  };

  // Success view state
  const [showSuccessView, setShowSuccessView] = useState(false);
  const [infoSnackOpen, setInfoSnackOpen] = useState(false);

  // Theme support for Paper
  const themeMode = React.useContext(
    require("context/ThemeContext").ThemeContext
  );
  const darkMode = themeMode.state.darkMode;

  if (!context.state?.isAuthenticated) {
    return (
      <Dialog open={open} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 22 }}>
          Upload Prescription
        </DialogTitle>
        <DialogContent sx={{ py: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Please login or register to upload your prescription.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              handleDialogClose();
              const currentPath = location.pathname + location.search;
              redirectToLoginWithReturn(navigate, currentPath);
            }}
            sx={{ mt: 2, px: 4, fontWeight: 600 }}
          >
            Go to Login / Register
          </Button>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: darkMode
              ? "linear-gradient(135deg, #232526 0%, #414345 100%)"
              : "linear-gradient(135deg, #fff 0%, #f5f7fa 100%)",
            boxShadow: darkMode
              ? "0 8px 32px 0 rgba(31,38,135,0.37)"
              : "0 8px 32px 0 rgba(60,60,60,0.07)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 3,
            pt: 2,
          }}
        >
          <DialogTitle sx={{ p: 0, fontWeight: 700, fontSize: 22 }}>
            Upload Prescription
          </DialogTitle>
          <IconButton onClick={handleDialogClose} size="large">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ pt: 1, pb: 0, minHeight: 320 }}>
          {showSuccessView ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 260,
                py: 4,
              }}
            >
              <Fade in={showSuccessView}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      background: darkMode ? "#2e7d32" : "#e8f5e9",
                      borderRadius: "50%",
                      width: 72,
                      height: 72,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: 2,
                    }}
                  >
                    <SaveIcon
                      sx={{
                        color: darkMode ? "#fff" : "#43a047",
                        fontSize: 40,
                      }}
                    />
                  </Box>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 1,
                      color: darkMode ? "#fff" : "#2e7d32",
                    }}
                  >
                    Prescription uploaded successfully!
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: darkMode ? "#b2dfdb" : "#1565c0",
                      mb: 2,
                      textAlign: "center",
                    }}
                  >
                    Our Pharmacist will call you within 30 minutes.
                  </Typography>
                </Box>
              </Fade>
            </Box>
          ) : (
            <form
              id="user-files-upload-form"
              onSubmit={handleSubmit}
              autoComplete="off"
            >
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <ImageUploader
                    imageArray={mediaArray}
                    setImageArray={setMediaArray}
                    multiple={false}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="File Type"
                    value={fileType}
                    onChange={(e) => setFileType(e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                  >
                    {fileTypeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Prescription for"
                    value={fileFor}
                    onChange={(e) => setFileFor(e.target.value)}
                    fullWidth
                    required
                    variant="outlined"
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      background: darkMode
                        ? "rgba(255,255,255,0.03)"
                        : "#f7f8fa",
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      sx={{ mb: 2, fontWeight: 600 }}
                    >
                      Patient Details
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          select
                          label="Gender"
                          name="Gender"
                          value={details.Gender}
                          onChange={handleDetailsChange}
                          fullWidth
                          required
                          variant="outlined"
                        >
                          {genderOptions.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <TextField
                          label="Age"
                          name="Age"
                          value={details.Age}
                          onChange={handleDetailsChange}
                          type="number"
                          fullWidth
                          required
                          variant="outlined"
                          inputProps={{ min: 0, max: 120 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Symptoms"
                          name="Symptoms"
                          value={details.Symptoms}
                          onChange={handleDetailsChange}
                          fullWidth
                          multiline
                          rows={2}
                          variant="outlined"
                          inputProps={{ maxLength: 250 }}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          label="Notes"
                          name="Notes"
                          value={details.Notes}
                          onChange={handleDetailsChange}
                          fullWidth
                          multiline
                          rows={5}
                          variant="outlined"
                          inputProps={{ maxLength: 500 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </form>
          )}
        </DialogContent>
        {!showSuccessView && (
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              form="user-files-upload-form"
              disabled={processing}
              startIcon={
                processing ? <CircularProgress size={20} /> : <SaveIcon />
              }
              sx={{
                borderRadius: 2,
                fontWeight: 600,
                px: 4,
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              }}
            >
              Save
            </Button>
          </DialogActions>
        )}
      </Dialog>
      <SnackBar
        open={snackOpen}
        setOpen={setSnackOpen}
        options={snackOptions}
      />
      <SnackBar
        open={infoSnackOpen}
        setOpen={setInfoSnackOpen}
        options={{
          color: "info",
          message: "Our Pharmacist will call you within 30 minutes.",
        }}
      />
    </>
  );
}
